import { useLayoutEffect, useRef, useState } from 'react';

import { colour, space, text } from './theme';
import { useDismissOnOutside } from './useDismissOnOutside';

/**
 * The menu a right-click opens on the canvas.
 *
 * ## Why the shell builds the items and this does not
 *
 * Because what a right-click should offer is a question about the drawing, and this file knows
 * nothing about drawings. It takes a list and places it, which is the whole of its job — and it
 * keeps every decision about *what* to offer beside the editor state that answers it, rather
 * than half there and half here.
 *
 * ## Placing it
 *
 * Flipped rather than clamped when it would run off an edge. Clamping slides the menu until it
 * fits, which drags it under the cursor and leaves the first item sitting where the pointer
 * already is — one twitch from being chosen by accident. Flipping keeps the corner the user
 * aimed at, which is the corner they are looking at.
 */

export interface MenuItem {
  readonly label: string;
  /** The keystroke that does the same thing, shown greyed at the right. */
  readonly shortcut?: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  /** A rule above this item, grouping what follows. */
  readonly separated?: boolean;
  /** Deletion, shown in the warning colour. */
  readonly destructive?: boolean;
}

export interface ContextMenuProps {
  readonly at: { readonly x: number; readonly y: number };
  readonly items: readonly MenuItem[];
  readonly onClose: () => void;
}

const EDGE = 8;

export function ContextMenu({ at, items, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [placed, setPlaced] = useState(at);

  useLayoutEffect(() => {
    const node = ref.current;
    if (node === null) return;

    const box = node.getBoundingClientRect();
    setPlaced({
      x: at.x + box.width + EDGE > window.innerWidth ? Math.max(EDGE, at.x - box.width) : at.x,
      y: at.y + box.height + EDGE > window.innerHeight ? Math.max(EDGE, at.y - box.height) : at.y,
    });
  }, [at]);

  // The press that dismisses it is swallowed, and Escape and a lost window close it too. See
  // `useDismissOnOutside`, which the export menu and the hints panel share.
  useDismissOnOutside(ref, onClose);

  if (items.length === 0) return null;

  return (
    <div
      ref={ref}
      role="menu"
      className="es-menu"
      style={{ position: 'fixed', left: placed.x, top: placed.y, zIndex: 60, minWidth: 210 }}
    >
      {items.map((item) => (
        <div key={item.label}>
          {item.separated === true && (
            <div
              className="es-divider"
              style={{ height: 1, margin: `${String(space.tight)}px 0` }}
            />
          )}
          <button
            type="button"
            role="menuitem"
            className="es-menu-item"
            disabled={item.disabled === true}
            onClick={() => {
              item.onSelect();
              onClose();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: space.loose,
              ...(item.destructive === true ? { color: colour.danger } : {}),
              ...(item.disabled === true ? { opacity: 0.4, cursor: 'default' } : {}),
            }}
          >
            <span>{item.label}</span>
            {item.shortcut !== undefined && (
              <span style={{ color: colour.faint, font: text.small }}>{item.shortcut}</span>
            )}
          </button>
        </div>
      ))}
    </div>
  );
}
